import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICE_DIVISIONS, getServicesByDivision } from "../../config/services";
import { getStatusMeta } from "../../config/statuses";
import { resolveIcon } from "../../config/icons";

/**
 * Engineering & Technology Services overview — replaces prior Divisions framing
 * with commercially accurate service divisions.
 */
export default function EngineeringServices() {
  return (
    <section
      id="divisions"
      className="relative py-20 sm:py-28 lg:py-36 bg-secondary/40 border-y border-border scroll-mt-[88px]"
      data-testid="engineering-services"
    >
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
          <div className="lg:col-span-7">
            <div className="sec-index mb-4">02 — Services</div>
            <h2 className="display display-section text-4xl md:text-6xl">
              Engineering &amp;{" "}
              <span style={{ color: "var(--gold)" }}>Technology</span> Services.
            </h2>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <p className="text-base md:text-lg text-steel-text leading-relaxed">
              Four primary divisions covering industrial automation, electrical
              engineering, electronics engineering and digital platforms —
              engaged through a clear quotation workflow with DEBSUN Technologies.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {SERVICE_DIVISIONS.map((division, i) => {
            const Icon = resolveIcon(division.icon);
            const status = getStatusMeta(division.status);
            const services = getServicesByDivision(division.id);
            return (
              <motion.div
                key={division.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.7,
                  delay: (i % 2) * 0.1,
                  ease: [0.19, 1, 0.22, 1],
                }}
                className="steel-card clip-frame group p-7 sm:p-9 lg:p-11 flex flex-col min-h-[300px] sm:min-h-[340px] h-full"
                data-testid={`service-division-${division.id}`}
              >
                <div className="flex items-start justify-between">
                  <Icon
                    size={40}
                    strokeWidth={1.2}
                    className="text-[color:var(--steel)] group-hover:text-[color:var(--gold)] transition-colors duration-500"
                  />
                  <div className="flex items-center gap-2">
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: status.color }}
                    />
                    <span
                      className="font-mono-2 text-[10px] tracking-[0.2em] uppercase"
                      style={{ color: status.color }}
                    >
                      {status.label}
                    </span>
                  </div>
                </div>

                <div className="mt-auto pt-10">
                  <div className="font-mono-2 text-[10px] tracking-[0.3em] text-muted-foreground mb-2">
                    {division.code}
                  </div>
                  <h3 className="display display-section text-2xl md:text-3xl">
                    {division.name}
                  </h3>
                  <p className="mt-4 text-sm text-steel-text leading-relaxed max-w-lg">
                    {division.description}
                  </p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {services.slice(0, 5).map((s) => (
                      <span
                        key={s.id}
                        className="font-mono-2 text-[10px] tracking-[0.15em] uppercase px-2.5 py-1 border border-border text-muted-foreground"
                      >
                        {s.name}
                      </span>
                    ))}
                    {services.length > 5 && (
                      <span className="font-mono-2 text-[10px] tracking-[0.15em] uppercase px-2.5 py-1 border border-border text-muted-foreground">
                        +{services.length - 5} more
                      </span>
                    )}
                  </div>
                  <Link
                    to={`/services#${division.id}`}
                    className="mt-8 inline-flex items-center gap-2 font-mono-2 text-[11px] tracking-[0.2em] uppercase"
                    style={{ color: "var(--gold)" }}
                    data-testid={`division-link-${division.id}`}
                  >
                    View division <ArrowUpRight size={14} />
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-10 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between border border-border bg-background/60 p-6">
          <p className="text-sm text-steel-text leading-relaxed max-w-2xl">
            Engineering services are quoted through DEBSUN Technologies. Large-scale
            EPC and turnkey execution is handled separately through our strategic
            engineering partner.
          </p>
          <Link
            to="/services"
            className="btn-gold clip-frame-sm px-5 py-3 text-[11px] uppercase tracking-widest font-mono-2 inline-flex items-center gap-2 shrink-0"
            data-testid="all-services-cta"
          >
            All Services <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}
